import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money } from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Users, Plus, Search, ChevronRight } from "lucide-react";

const cBlank = { name: "", phone: "", email: "", notes: "" };

export default function Customers() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [cOpen, setCOpen] = useState(false);
  const [cForm, setCForm] = useState(cBlank);

  const { data: customers = [] } = useQuery({
    queryKey: ["mech-customers"],
    queryFn: async () => (await api.get("/customers")).data,
  });
  const { data: vehicles = [] } = useQuery({
    queryKey: ["mech-ext"],
    queryFn: async () => (await api.get("/vehicles")).data,
  });

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return customers;
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(s)
      || (c.phone || "").toLowerCase().includes(s)
      || (c.email || "").toLowerCase().includes(s)
      || vehicles.some((v) => v.customer_id === c.id && (
        (v.plate || "").toLowerCase().includes(s)
        || (v.vin || "").toLowerCase().includes(s)
        || `${v.year || ""} ${v.make || ""} ${v.model || ""}`.toLowerCase().includes(s)
      ))
    );
  }, [q, customers, vehicles]);

  const vehicleCountFor = (cid) => vehicles.filter((v) => v.customer_id === cid).length;

  const saveCust = useMutation({
    mutationFn: () => api.post("/customers", cForm),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["mech-customers"] });
      toast.success("Customer created");
      setCOpen(false);
      setCForm(cBlank);
      // Auto-navigate into the new customer's page so the user can add the car right away
      const id = r?.data?.id;
      if (id) navigate(`/mechanic/customers/${id}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <div className="p-8 space-y-6" data-testid="mech-customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl mb-1">Customers</h1>
          <p className="text-muted-foreground">Search by name, phone, email, plate, VIN, or vehicle.</p>
        </div>
        <Button onClick={() => { setCForm(cBlank); setCOpen(true); }} data-testid="new-customer-button">
          <Plus className="h-4 w-4 mr-2" />New customer
        </Button>
      </div>

      <div className="relative max-w-xl">
        <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search customers…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="customer-search"
        />
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="text-right">Cars</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                {q ? `No customers match "${q}"` : "No customers yet."}
              </TableCell></TableRow>
            ) : filtered.map((c) => (
              <TableRow
                key={c.id}
                className="cursor-pointer hover:bg-secondary/40"
                onClick={() => navigate(`/mechanic/customers/${c.id}`)}
                data-testid={`customer-row-${c.id}`}
              >
                <TableCell className="font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" />{c.name}</TableCell>
                <TableCell className="text-sm">{c.phone || "—"}</TableCell>
                <TableCell className="text-sm">{c.email || "—"}</TableCell>
                <TableCell className="text-right font-mono-data">{vehicleCountFor(c.id)}</TableCell>
                <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={cOpen} onOpenChange={setCOpen}>
        <DialogContent data-testid="customer-dialog">
          <DialogHeader><DialogTitle>New customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="label-mono">Name *</Label><Input value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} data-testid="cust-name" /></div>
            <div><Label className="label-mono">Phone</Label><Input value={cForm.phone} onChange={(e) => setCForm({ ...cForm, phone: e.target.value })} data-testid="cust-phone" /></div>
            <div><Label className="label-mono">Email</Label><Input value={cForm.email} onChange={(e) => setCForm({ ...cForm, email: e.target.value })} data-testid="cust-email" /></div>
            <div><Label className="label-mono">Notes</Label><Input value={cForm.notes} onChange={(e) => setCForm({ ...cForm, notes: e.target.value })} /></div>
            <p className="text-xs text-muted-foreground">You&apos;ll be taken to the customer&apos;s page to add their car(s).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCOpen(false)}>Cancel</Button>
            <Button onClick={() => saveCust.mutate()} disabled={saveCust.isPending || !cForm.name.trim()} data-testid="cust-save">
              {saveCust.isPending ? "Saving…" : "Create & open"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
